import { type GetCaseRecordResponse, WorkflowState } from '@compass/client/types/case_record';
import type { Workflow } from '@compass/client/types/workflow';

import { extractPayerHoursSlug, resolvePayerLabel } from '@/utils/payer';
import { calculateSLA } from '@/utils/sla';
import { getStatusLabel } from '@/utils/status';

import { deriveRiskLevel } from '../../data/risk';
import type { CaseItem } from '../../data/types';
import { ASSIGNED_TO_ME_ID } from '../../data/types';

export interface TranslateContext {
  programNameById: Map<string, string>;
  workflowsById: Map<string, Workflow>;
  slaMap: Map<string, number>;
  payorNameBySlug: Map<string, string>;
  currentUserId: string | null;
  selectedOrganizationId: string | null | undefined;
  /** workflow_type → (disposition_code → label). Pre-resolved by
   *  `useWorkforceCases` via `@100mslive/disposition-engine` so the
   *  translator stays pure. Workflow types without a catalog are absent. */
  dispositionLabelsByWorkflowType: Map<string, Map<number, string>>;
}

/** Minutes elapsed since `iso`, floored. Invalid / missing inputs → 0. */
export function minutesSince(iso: string | undefined | null): number {
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 0;
  return Math.max(0, Math.floor((Date.now() - ts) / 60_000));
}

export function resolveAssigneeId(rawAssignedUserId: string, currentUserId: string | null): string {
  if (currentUserId && rawAssignedUserId === currentUserId) return ASSIGNED_TO_ME_ID;
  return rawAssignedUserId;
}

export function resolveProgramName(
  programId: string | undefined,
  byId: Map<string, string>,
): string {
  if (!programId) return '—';
  return byId.get(programId) ?? programId;
}

export function resolveStatusLabel(
  status: string,
  workflowId: string | undefined,
  workflowsById: Map<string, Workflow>,
): string {
  if (!status) return '';
  const workflow = workflowId ? workflowsById.get(workflowId) : undefined;
  return getStatusLabel(status, workflow);
}

function deriveSla(c: GetCaseRecordResponse, slaMap: Map<string, number>) {
  return calculateSLA(
    c.workflow_last_started_at,
    c.workflow_duration_seconds,
    c.workflow_id ? slaMap.get(c.workflow_id) : undefined,
    c.status_flags?.sla_breach,
    c.workflow_state,
  );
}

/** Coerce empty/null/undefined to `undefined` for optional string fields. */
export function emptyToUndefined(v: string | undefined | null): string | undefined {
  return v || undefined;
}

/** Resolve `disposition_code` → human-readable label via the workflow's
 *  catalog. Pulled out of `buildCaseItem` to keep the translator's
 *  cyclomatic complexity below the lint budget. */
export function resolveDispositionLabel(
  c: GetCaseRecordResponse,
  workflowType: string,
  catalogByType: Map<string, Map<number, string>>,
): string | undefined {
  const code = c.disposition_code;
  if (typeof code !== 'number') return undefined;
  return catalogByType.get(workflowType)?.get(code);
}

/** Payor slug the case's AI calls dial; keys the clock popover's payer-hours
 *  fetch (BV-2525). Undefined when the case names no payor. */
function resolvePayerHoursSlug(c: GetCaseRecordResponse, workflowType: string): string | undefined {
  return emptyToUndefined(extractPayerHoursSlug(workflowType, c.input_data?.data));
}

/** Number of output keys still outstanding for a complete disposition. */
export function countMissingFields(c: GetCaseRecordResponse): number {
  return c.disposition?.missing_fields?.length ?? 0;
}

/** True once an auditor has started the case's human audit. */
function isHumanAuditInProgress(c: GetCaseRecordResponse): boolean {
  return c.status_flags?.human_audit_in_progress === true;
}

/**
 * Translate one raw case-record into the view-model the grid consumes.
 * Pure function of `c` + `ctx` — no React, no hooks. The orchestrating
 * memo lives in `useWorkforceCases`.
 */
export function buildCaseItem(c: GetCaseRecordResponse, ctx: TranslateContext): CaseItem {
  const status = c.state ?? '';
  const workflowType = c.workflow_type ?? '';
  const sla = deriveSla(c, ctx.slaMap);
  return {
    id: c.id ?? '',
    payer: resolvePayerLabel(workflowType, c.input_data?.data, ctx.payorNameBySlug),
    program: resolveProgramName(c.program_id, ctx.programNameById),
    orgName: '',
    status,
    statusLabel: resolveStatusLabel(status, c.workflow_id, ctx.workflowsById),
    stageMinutes: minutesSince(c.state_updated_at),
    slaDeltaMinutes: sla.remainingMinutes ?? 0,
    riskLevel: deriveRiskLevel({
      isBreached: sla.isBreached,
      percentageElapsed: sla.percentageElapsed,
    }),
    slaPaused: c.workflow_state === WorkflowState.WORKFLOW_STATE_PAUSED,
    assigneeId: resolveAssigneeId(c.assigned_user_id ?? '', ctx.currentUserId),
    teamId: c.team_id ?? '',
    workflowType,
    activeCallId: emptyToUndefined(c.active_call_id),
    humanAuditInProgress: isHumanAuditInProgress(c),
    programId: emptyToUndefined(c.program_id),
    organizationId: emptyToUndefined(c.organization_id) ?? ctx.selectedOrganizationId ?? undefined,
    dispositionLabel: resolveDispositionLabel(c, workflowType, ctx.dispositionLabelsByWorkflowType),
    missingFieldsCount: countMissingFields(c),
    aiCallAttempts: c.ai_call_attempts,
    humanCallAttempts: c.human_call_attempts,
    payerSlug: resolvePayerHoursSlug(c, workflowType),
  };
}
